import { useEffect, useState } from "react";
import { fetchBreakdown, send, type BreakdownData } from "../api.js";

/**
 * Who can do what.
 *
 * An empty matrix is not a broken one: the compiler treats "no skills recorded"
 * as "skills are not in play", so the shop schedules exactly as it did before
 * this screen existed. Filling a row in is what starts narrowing eligibility,
 * which is why the note below says so rather than leaving it to be discovered.
 */
export function Skills() {
  const [data, setData] = useState<BreakdownData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    try { setData(await fetchBreakdown()); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  if (err && !data) return <div className="err">{err}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const isAdmin = data.user.role === "admin";
  // The matrix rows a person's proficiency, which is a resource.edit action —
  // a manager holds that for their own team, same as People.tsx. The skill
  // list itself (add/rename/delete a skill) is vocabulary.edit, admin-only,
  // and stays gated below.
  const canEditPerson = (r: { teamId: string | null }) =>
    isAdmin || (r.teamId !== null && data.user.managedTeamIds.includes(r.teamId));
  const people = data.resources.filter((r) => !r.isPool && r.active);
  const held = new Map(
    data.resourceSkills.map((rs) => [`${rs.resourceId}|${rs.skillId}`, rs.proficiency]),
  );
  const usedBy = new Map<string, number>();
  for (const p of data.phases) {
    if (p.skillId) usedBy.set(p.skillId, (usedBy.get(p.skillId) ?? 0) + 1);
  }

  const addSkill = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    void act(async () => { await send("/skills", "POST", { name: trimmed }); setName(""); });
  };

  const removeSkill = (skillId: string, label: string) =>
    act(async () => {
      try {
        await send(`/skills/${skillId}`, "DELETE");
      } catch (e) {
        const msg = (e as Error).message;
        if (!/require this skill/.test(msg) || !confirm(`${msg}. Remove it from them and delete “${label}”?`)) throw e;
        await send(`/skills/${skillId}`, "DELETE", { force: true });
      }
    });

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        A step can require a skill at a level. Only people holding it at that level or above
        are eligible for it — and if nobody is, the step falls back to its discipline and the
        solve says so rather than failing. An empty matrix means skills are not in play at all.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      {!data.skills.length ? (
        <p className="muted">No skills yet.</p>
      ) : (
        <div className="scroll tall">
          <table className="qt" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Person</th>
                {data.skills.map((s) => (
                  <th key={s.skillId} className="r" title={
                    usedBy.get(s.skillId)
                      ? `${usedBy.get(s.skillId)} step(s) require this`
                      : "no step requires this yet"
                  }>
                    {s.name}
                    <div className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
                      {usedBy.get(s.skillId) ?? 0} step{usedBy.get(s.skillId) === 1 ? "" : "s"}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((r) => (
                <tr key={r.resourceId} style={{ opacity: busy ? 0.6 : 1 }}>
                  <td><b>{r.name}</b> <span className="muted mono">
                    {r.disciplineGroup?.replace("01-DFTG", "") ?? ""}
                  </span></td>
                  {data.skills.map((s) => {
                    const level = held.get(`${r.resourceId}|${s.skillId}`) ?? 0;
                    return (
                      <td key={s.skillId} className="r">
                        <select value={level} disabled={!canEditPerson(r)}
                          onChange={(e) => void act(() =>
                            send(`/resources/${r.resourceId}/skills`, "POST", {
                              skillId: s.skillId, proficiency: Number(e.target.value),
                            }))}>
                          <option value={0}>—</option>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <div className="run-sec" style={{ marginTop: 16 }}>
          <h3>The skill list</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input placeholder="e.g. TCU panels" value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addSkill(); }} />
            <button className="btn on" disabled={busy || !name.trim()} onClick={addSkill}>
              Add a skill
            </button>
          </div>
          <ul className="reqs-list">
            {data.skills.map((s) => (
              <li key={s.skillId}>
                <div className="reqs-body">
                  <input defaultValue={s.name} disabled={busy}
                    onBlur={(e) => e.target.value !== s.name && void act(() =>
                      send(`/skills/${s.skillId}`, "PATCH", { name: e.target.value }))} />
                  <span className="muted" style={{ fontSize: 12 }}>
                    {usedBy.get(s.skillId) ?? 0} step{usedBy.get(s.skillId) === 1 ? "" : "s"} require it
                  </span>
                  <button className="btn" disabled={busy}
                    onClick={() => void removeSkill(s.skillId, s.name)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
